/**
 * Activity clipboard — pure serialisation / parsing for copy/paste of
 * activity rows between the platform and Excel (and platform → platform).
 *
 * Outbound (platform → Excel / platform → platform):
 *   - serializeToTSV(activities, visibleColumnKeys)
 *       → header row + one row per activity, tab-separated, CRLF lines,
 *         dates as yyyy-MM-dd, numbers as plain numbers, status as label.
 *   - serializeToHTML(activities, visibleColumnKeys)
 *       → <table> for Excel rich-paste with column structure preserved.
 *
 * Inbound (Excel → platform):
 *   - parseTSV(text) → array of { columnKey: rawString } rows.
 *   - parsedRowToActivity(row) → activity-create payload, or null if row
 *     has no usable name.
 *
 * Why this lives in /lib (not in the grid component): the logic is pure
 * and independently testable, mirroring the wbs_copy / bulk_edit pattern.
 */

import { format, parse, isValid, parseISO } from 'date-fns';

// ---------------------------------------------------------------------------
// Column metadata — single source of truth for clipboard payloads.
// Mirrors UnifiedScheduleView.DEFAULT_COLUMN_WIDTHS in display order, minus
// the `links` column (relationship icon — carries no tabular data).
// ---------------------------------------------------------------------------

export const CLIPBOARD_COLUMNS = [
  { key: 'activityId',     label: 'Activity ID',     type: 'string' },
  { key: 'activityName',   label: 'Activity Name',   type: 'string' },
  { key: 'resources',      label: 'Resources',       type: 'number' },
  { key: 'baselineStart',  label: 'Baseline Start',  type: 'date'   },
  { key: 'currentStart',   label: 'Start',           type: 'date'   },
  { key: 'duration',       label: 'Duration',        type: 'number' },
  { key: 'baselineFinish', label: 'Baseline Finish', type: 'date'   },
  { key: 'currentFinish',  label: 'Finish',          type: 'date'   },
  { key: 'actualStart',    label: 'Actual Start',    type: 'date'   },
  { key: 'actualFinish',   label: 'Actual Finish',   type: 'date'   },
  { key: 'expectedFinish', label: 'Expected Finish', type: 'date'   },
  { key: 'progress',       label: 'Progress',        type: 'number' },
  { key: 'float',          label: 'Float',           type: 'number' },
  { key: 'status',         label: 'Status',          type: 'enum'   },
];

const COLUMN_BY_KEY = Object.fromEntries(CLIPBOARD_COLUMNS.map(c => [c.key, c]));
const COLUMN_BY_LABEL = Object.fromEntries(CLIPBOARD_COLUMNS.map(c => [c.label, c]));

export const STATUS_LABELS = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  completed:   'Completed',
};

export const STATUS_REVERSE = Object.fromEntries(
  Object.entries(STATUS_LABELS).map(([k, v]) => [v, k])
);

// ---------------------------------------------------------------------------
// Date helpers
// ---------------------------------------------------------------------------

const ISO_DATE_FMT = 'yyyy-MM-dd';

const PARSE_FORMATS = [
  'yyyy-MM-dd',
  'MM/dd/yyyy',
  'M/d/yyyy',
  'dd/MM/yyyy',
  'd/M/yyyy',
];

function toIsoDate(value) {
  if (value == null || value === '') return '';
  // Backend dates arrive as 'YYYY-MM-DDTHH:mm:ss' or 'YYYY-MM-DD'.
  // Lop the time off if present — we only need the date part for Excel.
  if (typeof value === 'string') {
    const ymd = value.slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return ymd;
    // Fallback: try to parse via parseISO then format.
    try {
      const d = parseISO(value);
      if (isValid(d)) return format(d, ISO_DATE_FMT);
    } catch { /* fall through */ }
  }
  if (value instanceof Date && isValid(value)) {
    return format(value, ISO_DATE_FMT);
  }
  return '';
}

function parseDateString(value) {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  for (const fmt of PARSE_FORMATS) {
    const d = parse(trimmed, fmt, new Date());
    if (isValid(d)) return format(d, ISO_DATE_FMT);
  }
  // Last resort: parseISO accepts full timestamps too.
  try {
    const d = parseISO(trimmed);
    if (isValid(d)) return format(d, ISO_DATE_FMT);
  } catch { /* swallow */ }
  return null;
}

// ---------------------------------------------------------------------------
// Per-column value extraction (activity → display string)
// ---------------------------------------------------------------------------

function pickActivityRaw(activity, columnKey) {
  switch (columnKey) {
    case 'activityId':     return activity.activity_id ?? '';
    case 'activityName':   return activity.name ?? '';
    case 'resources':      return Array.isArray(activity.resource_ids) ? activity.resource_ids.length : 0;
    case 'baselineStart':  return activity.baseline_start;
    case 'baselineFinish': return activity.baseline_finish;
    case 'currentStart':   return activity.planned_start ?? activity.early_start ?? null;
    case 'currentFinish':  return activity.planned_finish ?? activity.early_finish ?? null;
    case 'actualStart':    return activity.actual_start;
    case 'actualFinish':   return activity.actual_finish;
    case 'expectedFinish': return activity.expected_finish;
    case 'duration':       return activity.original_duration;
    case 'progress':       return activity.percent_complete;
    case 'float':          return activity.total_float;
    case 'status':         return activity.status;
    default:               return null;
  }
}

export function formatCell(activity, column) {
  if (!activity || !column) return '';
  const raw = pickActivityRaw(activity, column.key);

  switch (column.type) {
    case 'date':
      return toIsoDate(raw);
    case 'number': {
      if (raw == null || raw === '') return '';
      const n = Number(raw);
      return Number.isFinite(n) ? String(n) : '';
    }
    case 'enum':
      return STATUS_LABELS[raw] ?? '';
    case 'string':
    default: {
      if (raw == null) return '';
      // Strip tab/CR/LF so a single cell never breaks TSV row alignment.
      return String(raw).replace(/[\t\r\n]+/g, ' ');
    }
  }
}

// ---------------------------------------------------------------------------
// Outbound — TSV
// ---------------------------------------------------------------------------

function pickColumns(visibleColumnKeys) {
  if (!visibleColumnKeys || visibleColumnKeys.length === 0) {
    return CLIPBOARD_COLUMNS;
  }
  // Preserve CLIPBOARD_COLUMNS canonical order; intersect with visible set.
  const visibleSet = new Set(visibleColumnKeys);
  return CLIPBOARD_COLUMNS.filter(c => visibleSet.has(c.key));
}

export function serializeToTSV(activities, visibleColumnKeys) {
  const cols = pickColumns(visibleColumnKeys);
  const header = cols.map(c => c.label).join('\t');
  const rows = (activities || []).map(act =>
    cols.map(c => formatCell(act, c)).join('\t')
  );
  return [header, ...rows].join('\r\n');
}

// ---------------------------------------------------------------------------
// Outbound — HTML <table> for Excel rich paste
// ---------------------------------------------------------------------------

function htmlEscape(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function serializeToHTML(activities, visibleColumnKeys) {
  const cols = pickColumns(visibleColumnKeys);
  const headRow =
    '<tr>' + cols.map(c => `<th>${htmlEscape(c.label)}</th>`).join('') + '</tr>';
  const bodyRows = (activities || []).map(act => {
    const cells = cols.map(c => `<td>${htmlEscape(formatCell(act, c))}</td>`).join('');
    return `<tr>${cells}</tr>`;
  }).join('');
  return `<table><thead>${headRow}</thead><tbody>${bodyRows}</tbody></table>`;
}

// ---------------------------------------------------------------------------
// Inbound — TSV parsing
// ---------------------------------------------------------------------------

// When pasted data has no recognisable header row, treat the columns in
// this fixed positional order — chosen because these are the fields a user
// is most likely to type/paste from a plain spreadsheet.
const HEADERLESS_COLUMN_ORDER = [
  'activityName',
  'currentStart',
  'currentFinish',
  'duration',
  'progress',
  'status',
];

function looksLikeHeaderRow(cells) {
  // Header detection: at least one cell matches a known column label.
  return cells.some(cell => COLUMN_BY_LABEL[cell.trim()]);
}

export function parseTSV(text) {
  if (!text || typeof text !== 'string' || !text.trim()) return [];

  const lines = text
    .split(/\r\n|\n/)
    .map(line => line.replace(/\r$/, ''))
    .filter(line => line.trim().length > 0);

  if (lines.length === 0) return [];

  const firstCells = lines[0].split('\t');
  const hasHeader = looksLikeHeaderRow(firstCells);

  let columnKeys;
  let dataLines;
  if (hasHeader) {
    columnKeys = firstCells.map(label => {
      const col = COLUMN_BY_LABEL[label.trim()];
      return col ? col.key : null;
    });
    dataLines = lines.slice(1);
  } else {
    columnKeys = HEADERLESS_COLUMN_ORDER;
    dataLines = lines;
  }

  return dataLines.map(line => {
    const cells = line.split('\t');
    const row = {};
    for (let i = 0; i < cells.length; i++) {
      const key = columnKeys[i];
      if (key) row[key] = cells[i];
    }
    return row;
  });
}

// ---------------------------------------------------------------------------
// Inbound — coerce a parsed row into a create payload
// ---------------------------------------------------------------------------

export function parsedRowToActivity(row) {
  if (!row) return null;
  const name = (row.activityName ?? '').trim();
  if (!name) return null;

  const out = { name };

  if (row.activityId && row.activityId.trim()) {
    out.activity_id = row.activityId.trim();
  }

  const start = parseDateString(row.currentStart);
  if (start) out.planned_start = start;
  const finish = parseDateString(row.currentFinish);
  if (finish) out.planned_finish = finish;

  const actualStart = parseDateString(row.actualStart);
  if (actualStart) out.actual_start = actualStart;
  const actualFinish = parseDateString(row.actualFinish);
  if (actualFinish) out.actual_finish = actualFinish;

  // Duration: default to 1 (matches handleCreateActivity in UnifiedScheduleView).
  if (row.duration && row.duration.trim()) {
    const n = Number(row.duration.trim());
    if (Number.isFinite(n)) {
      out.original_duration = n;
      out.remaining_duration = n;
    } else {
      out.original_duration = 1;
      out.remaining_duration = 1;
    }
  } else {
    out.original_duration = 1;
    out.remaining_duration = 1;
  }

  if (row.progress && row.progress.trim()) {
    const n = Number(row.progress.trim());
    if (Number.isFinite(n)) out.percent_complete = n;
  }

  if (row.status && row.status.trim()) {
    const enumValue = STATUS_REVERSE[row.status.trim()];
    if (enumValue) out.status = enumValue;
  }

  return out;
}
